import axios from "axios";
import jwtDefaultConfig from "./jwtDefaultConfig";
// import { handleLogout } from "@store/actions/auth";

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig };

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false;

  // ** For Refreshing Token
  subscribers = [];

  constructor(jwtOverrideConfig) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig };

    // ** Request Interceptor
    axios.interceptors.request.use(
      (config) => {
        config.baseURL = process.env.REACT_APP_API_BASE_URL;

        // ** Get token from localStorage
        const accessToken = this.getToken();

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // ** Add request/response interceptor
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // ** const { config, response: { status } } = error
        const { config, response } = error;
        const originalRequest = config;

        // ** if (status === 401) {
        if (response && response.status === 401) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true;
            this.refreshToken()
              .then((r) => {
                this.isAlreadyFetchingAccessToken = false;
                // ** Update accessToken in localStorage
                this.setToken(r.data.result.accessToken);
                this.setRefreshToken(r.data.result.refreshToken);
                this.onAccessTokenFetched(r.data.result.accessToken);
              })
              .catch((err) => {
                // location.href = "/login";

                // handleLogout();
                localStorage.removeItem("userData");
                localStorage.removeItem(config.storageTokenKeyName);
                localStorage.removeItem(config.storageRefreshTokenKeyName);
              });
          }
          const retryOriginalRequest = new Promise((resolve) => {
            this.addSubscriber((accessToken) => {
              // ** Make sure to assign accessToken according to your response.
              // ** Check: https://pixinvent.ticksy.com/ticket/2413870
              // ** Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`;
              resolve(this.axios(originalRequest));
            });
          });
          return retryOriginalRequest;
        }
        return Promise.reject(error);
      }
    );
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter((callback) =>
      callback(accessToken)
    );
  }

  addSubscriber(callback) {
    this.subscribers.push(callback);
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName);
  }

  getUser() {
    return localStorage.getItem("userData");
  }

  getRefreshToken() {
    const refreshToken = localStorage.getItem(
      this.jwtConfig.storageRefreshTokenKeyName
    );

    return refreshToken;
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value);
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value);
  }

  async login(...args) {
    return axios.post(this.jwtConfig.loginEndpoint, ...args);
  }

  async impersonatedAuthenticate(value) {
    return axios.post(
      `${this.jwtConfig.impersonateEndpoint}?impersonationToken=${value}`
    );
  }

  register(...args) {
    return axios.post(this.jwtConfig.registerEndpoint, ...args);
  }

  refreshToken() {
    return axios.post(
      `${this.jwtConfig.refreshEndpoint}?refreshToken=${this.getRefreshToken()}`
    );
  }

  logout() {
    localStorage.removeItem("lastactivitytime");
    localStorage.removeItem("userData");
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
  }

  // Microsoft
  async checkOAuthMail(data) {
    return axios.get("/TokenAuth/CheckOAuthMail", {
      headers: {
        "Content-Type": "application/json",
      },
      params: data,
    });
  }

  // ** 2FA Setup Methods
  async setAuthenticatorKey(data) {
    return axios.post("/TokenAuth/SetAuthenticatorKey", data);
  }

  async setAuthenticatorKeyOnAuth(data) {
    return axios.post("/TokenAuth/SetAuthenticatorKeyOnAuth", data);
  }

  async setEmailAuthenticatorSetting(data) {
    return axios.post("/TokenAuth/SetEmailAuthenticatorSetting", data);
  }

  async setEmailAuthenticatorSettingOnAuth() {
    return axios.post("/TokenAuth/SetEmailAuthenticatorSettingOnAuth");
  }

  async resetTwoFactorAuthenticator(data) {
    return axios.post("/TokenAuth/ResetTwoFactorAuthenticator", data);
  }

  async getGoogleAuthImageUrl() {
    return axios.get("/TokenAuth/GetAuthenticatorSetupData", {
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async externalAuthenticate(data) {
    return axios.post("/TokenAuth/ExternalAuthenticate", data);
  }
}
